"use client"

import { create } from "zustand"
import { persist } from "zustand/middleware"
import type { Vehicle, MaintenanceRecord, Purchase, Alert, User, CalendarEvent, InspectionTicket, InspectionChecklist, CashBox, CashMovement, Zone, Report } from "@/types"
import { mockVehicles, mockMaintenanceRecords, mockPurchases, mockAlerts, mockUsers, mockCalendarEvents } from "./data"

interface AppState {
  _hasHydrated: boolean
  setHasHydrated: (state: boolean) => void

  user: User | null
  users: User[]
  isAuthenticated: boolean
  vehicles: Vehicle[]
  maintenanceRecords: MaintenanceRecord[]
  purchases: Purchase[]
  alerts: Alert[]
  calendarEvents: CalendarEvent[]
  inspectionTickets: InspectionTicket[]
  cashBoxes: CashBox[]
  cashMovements: CashMovement[]
  cashMovementsMeta: {
    totalCount: number
    totalPages: number
    currentPage: number
    limit: number
  } | null
  pendingMovements: CashMovement[]
  zones: Zone[]
  reports: Report[]

  notificationSettings: {
    maintenanceAlerts: boolean
    documentExpiry: boolean
    weeklyReport: boolean
  }

  // Zone actions
  addZone: (zone: Zone) => void
  deleteZone: (id: string) => void
  setZonesFromDb: (zones: Zone[]) => void

  // Auth actions
  login: (username: string, password: string) => boolean
  logout: () => void

  // User management actions (admin only)
  addUser: (user: Omit<User, "id" | "createdAt">) => void
  updateUser: (id: string, user: Partial<User>) => void
  deleteUser: (id: string) => void
  toggleUserActive: (id: string) => void
  updateProfile: (data: Partial<User>) => void

  // Vehicle actions
  addVehicle: (vehicle: Omit<Vehicle, "id">) => void
  updateVehicle: (id: string, vehicle: Partial<Vehicle>) => void
  deleteVehicle: (id: string) => void

  // Maintenance actions
  addMaintenance: (record: Omit<MaintenanceRecord, "id">) => void
  updateMaintenance: (id: string, record: Partial<MaintenanceRecord>) => void
  deleteMaintenance: (id: string) => void

  // Purchase actions
  addPurchase: (purchase: Omit<Purchase, "id">) => void
  updatePurchase: (id: string, purchase: Partial<Purchase>) => void
  deletePurchase: (id: string) => void

  // Alert actions
  addAlert: (alert: Omit<Alert, "id">) => void
  updateAlert: (id: string, alert: Partial<Alert>) => void
  dismissAlert: (id: string) => void
  reactivateAlert: (id: string) => void
  deleteAlert: (id: string) => void

  // Calendar Event actions
  addCalendarEvent: (event: Omit<CalendarEvent, "id">) => void
  updateCalendarEvent: (id: string, event: Partial<CalendarEvent>) => void
  deleteCalendarEvent: (id: string) => void

  // Inspection Ticket actions
  addInspectionTicket: (ticket: Omit<InspectionTicket, "id" | "ticketNumber" | "createdAt">) => void
  addInspectionTicketSync: (ticket: InspectionTicket) => void
  updateInspectionTicket: (id: string, ticket: Partial<InspectionTicket>) => void
  deleteInspectionTicket: (id: string) => void
  getNextTicketNumber: () => number

  // Cash management actions
  addCashBox: (cashBox: Omit<CashBox, "id" | "createdAt"> | CashBox) => void
  updateCashBoxBalance: (id: string, amount: number) => void
  deleteCashBox: (id: string) => void
  addCashMovement: (movement: Omit<CashMovement, "id" | "createdAt"> | CashMovement) => void
  updateCashMovement: (id: string, data: Partial<CashMovement>) => void
  deleteCashMovement: (id: string) => void
  transferFunds: (fromBoxId: string, toBoxId: string, amount: number, userId: string) => void
  getDriverCashBox: (userId: string) => CashBox | undefined
  getGeneralCashBox: () => CashBox | undefined

  // Report actions
  addReport: (report: Omit<Report, "id" | "createdAt" | "status">) => void
  updateReport: (id: string, report: Partial<Report>) => void
  deleteReport: (id: string) => void
  setReportsFromDb: (reports: any) => void

  // Settings actions
  updateNotificationSettings: (settings: Partial<AppState["notificationSettings"]>) => void

  // Database sync actions
  setUsersFromDb: (users: User[]) => void
  setVehiclesFromDb: (vehicles: Vehicle[]) => void
  setMaintenanceFromDb: (records: any) => void
  setPurchasesFromDb: (purchases: any) => void
  setAlertsFromDb: (alerts: Alert[]) => void
  setCashBoxesFromDb: (boxes: CashBox[]) => void
  setCashMovementsFromDb: (movementsData: any) => void
  setPendingMovementsFromDb: (movements: CashMovement[]) => void
  setInspectionTicketsFromDb: (tickets: any) => void

  // Data management actions
  exportAllData: () => {
    vehicles: Vehicle[]
    maintenanceRecords: MaintenanceRecord[]
    purchases: Purchase[]
    alerts: Alert[]
    calendarEvents: CalendarEvent[]
  }
  importAllData: (data: {
    vehicles?: Vehicle[]
    maintenanceRecords?: MaintenanceRecord[]
    purchases?: Purchase[]
    alerts?: Alert[]
    calendarEvents?: CalendarEvent[]
  }) => void
  clearAllData: () => void
}

export const useStore = create<AppState>()(
  persist(
    (set, get) => ({
      _hasHydrated: false,
      setHasHydrated: (state) => set({ _hasHydrated: state }),

      user: null,
      users: [],
      isAuthenticated: false,
      vehicles: [],
      maintenanceRecords: [],
      purchases: [],
      alerts: [],
      calendarEvents: [],
      inspectionTickets: [],
      cashBoxes: [
        {
          id: "cb-general",
          userId: null,
          type: "general" as const,
          balance: 10000,
          name: "Caja General",
          createdAt: "2024-01-01",
        }
      ],
      cashMovements: [],
      cashMovementsMeta: null,
      pendingMovements: [],
      reports: [],
      zones: [],
      notificationSettings: {
        maintenanceAlerts: true,
        documentExpiry: true,
        weeklyReport: false,
      },

      login: (username: string, password: string) => {
        const { users } = get()
        const foundUser = users.find(
          (u) => (u.username || "").toLowerCase() === username.toLowerCase() && u.password === password && u.active
        )
        if (foundUser) {
          set({ user: foundUser, isAuthenticated: true })
          return true
        }
        return false
      },

      logout: () => {
        set({
          user: null,
          isAuthenticated: false,
          vehicles: [],
          maintenanceRecords: [],
          purchases: [],
          alerts: [],
          calendarEvents: [],
          inspectionTickets: [],
          cashBoxes: [],
          cashMovements: [],
          pendingMovements: [],
          zones: [],
          reports: []
        });
        if (typeof window !== 'undefined') {
          localStorage.removeItem('fleet-management-storage');
          document.cookie = 'auth_token=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT;';
        }
      },

      addUser: (userData) => {
        const newUser: User = {
          ...userData,
          id: `u${Date.now()}`,
          createdAt: new Date().toISOString().split('T')[0],
        }
        set((state) => ({ users: [...state.users, newUser] }))
      },

      updateUser: (id, userData) => {
        set((state) => ({
          users: state.users.map((u) => (u.id === id ? { ...u, ...userData } : u)),
          // Also update current user if editing themselves
          user: state.user?.id === id ? { ...state.user, ...userData } : state.user,
        }))
      },

      deleteUser: (id) => {
        set((state) => ({
          users: state.users.filter((u) => u.id !== id),
        }))
      },

      toggleUserActive: (id) => {
        set((state) => ({
          users: state.users.map((u) =>
            u.id === id ? { ...u, active: !u.active } : u
          ),
        }))
      },

      updateProfile: (data) => {
        set((state) => {
          if (!state.user) return state
          const updatedUser = { ...state.user, ...data }
          return {
            user: updatedUser,
            users: state.users.map((u) => (u.id === state.user?.id ? updatedUser : u)),
          }
        })
      },

      addVehicle: (vehicle) => {
        const newVehicle: Vehicle = {
          ...vehicle,
          id: `v${Date.now()}`,
        }
        set((state) => ({ vehicles: [...state.vehicles, newVehicle] }))
      },

      updateVehicle: (id, vehicle) => {
        set((state) => ({
          vehicles: state.vehicles.map((v) => (v.id === id ? { ...v, ...vehicle } : v)),
        }))
      },

      deleteVehicle: (id) => {
        set((state) => ({
          vehicles: state.vehicles.filter((v) => v.id !== id),
          // Also delete related maintenance records and purchases
          maintenanceRecords: state.maintenanceRecords.filter((m) => m.vehicleId !== id),
          purchases: state.purchases.filter((p) => p.vehicleId !== id),
          alerts: state.alerts.filter((a) => a.vehicleId !== id),
        }))
      },

      addMaintenance: (record) => {
        const newRecord: MaintenanceRecord = {
          ...record,
          id: `m${Date.now()}`,
        }
        set((state) => ({
          maintenanceRecords: [...state.maintenanceRecords, newRecord],
        }))
      },

      updateMaintenance: (id, record) => {
        set((state) => ({
          maintenanceRecords: state.maintenanceRecords.map((m) => (m.id === id ? { ...m, ...record } : m)),
        }))
        fetch('/api/maintenance', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id, ...record }),
        }).catch(console.error)
      },

      deleteMaintenance: (id) => {
        set((state) => ({
          maintenanceRecords: state.maintenanceRecords.filter((m) => m.id !== id),
        }))
        fetch(`/api/maintenance?id=${id}`, {
          method: 'DELETE',
        }).catch(console.error)
      },

      addPurchase: (purchase) => {
        const newPurchase: Purchase = {
          ...purchase,
          id: `p${Date.now()}`,
        }
        set((state) => ({ purchases: [...state.purchases, newPurchase] }))
      },

      updatePurchase: (id, purchase) => {
        set((state) => ({
          purchases: state.purchases.map((p) => (p.id === id ? { ...p, ...purchase } : p)),
        }))
      },

      deletePurchase: (id) => {
        set((state) => ({
          purchases: state.purchases.filter((p) => p.id !== id),
        }))
      },

      addAlert: (alert) => {
        const newAlert: Alert = {
          ...alert,
          id: `a${Date.now()}`,
        }
        set((state) => ({ alerts: [...state.alerts, newAlert] }))
      },

      updateAlert: (id, alert) => {
        set((state) => ({
          alerts: state.alerts.map((a) => (a.id === id ? { ...a, ...alert } : a)),
        }))
      },

      dismissAlert: (id) => {
        set((state) => ({
          alerts: state.alerts.map((a) => (a.id === id ? { ...a, dismissed: true } : a)),
        }))
        fetch(`/api/alerts/${id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ dismissed: true })
        }).catch(err => console.error('Error descartando alerta:', err))
      },

      reactivateAlert: (id) => {
        set((state) => ({
          alerts: state.alerts.map((a) => (a.id === id ? { ...a, dismissed: false } : a)),
        }))
        fetch(`/api/alerts/${id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ dismissed: false })
        }).catch(err => console.error('Error reactivando alerta:', err))
      },

      deleteAlert: (id) => {
        set((state) => ({
          alerts: state.alerts.filter((a) => a.id !== id),
        }))
        fetch(`/api/alerts/${id}`, {
          method: 'DELETE'
        }).catch(err => console.error('Error eliminando alerta:', err))
      },

      addCalendarEvent: (event) => {
        const newEvent: CalendarEvent = {
          ...event,
          id: `e${Date.now()}`,
        }
        set((state) => ({ calendarEvents: [...state.calendarEvents, newEvent] }))
      },

      updateCalendarEvent: (id, event) => {
        set((state) => ({
          calendarEvents: state.calendarEvents.map((e) => (e.id === id ? { ...e, ...event } : e)),
        }))
      },

      deleteCalendarEvent: (id) => {
        set((state) => ({
          calendarEvents: state.calendarEvents.filter((e) => e.id !== id),
        }))
      },

      addInspectionTicket: (ticket) => {
        const ticketNumber = get().getNextTicketNumber()
        const newTicket = {
          ...ticket,
          id: `it${Date.now()}`,
          ticketNumber,
          createdAt: new Date().toISOString().split('T')[0],
        }
        set((state) => ({ inspectionTickets: [...state.inspectionTickets, newTicket] }))
      },

      updateInspectionTicket: (id, ticket) => {
        set((state) => ({
          inspectionTickets: state.inspectionTickets.map((t) => (t.id === id ? { ...t, ...ticket } : t)),
        }))
      },

      deleteInspectionTicket: (id) => {
        set((state) => ({
          inspectionTickets: state.inspectionTickets.filter((t) => t.id !== id),
        }))
      },

      addInspectionTicketSync: (ticket) => {
        set((state) => ({ inspectionTickets: [...state.inspectionTickets, ticket] }))
      },

      getNextTicketNumber: () => {
        const tickets = get().inspectionTickets
        if (tickets.length === 0) return 1
        return Math.max(...tickets.map(t => t.ticketNumber)) + 1
      },

      addCashBox: (cashBox) => {
        const newCashBox = {
          ...cashBox,
          id: (cashBox as any).id || `cb${Date.now()}`,
          createdAt: (cashBox as any).createdAt || new Date().toISOString().split('T')[0],
        }
        set((state) => ({ cashBoxes: [...state.cashBoxes, newCashBox as CashBox] }))
      },

      updateCashBoxBalance: (id, amount) => {
        set((state) => ({
          cashBoxes: state.cashBoxes.map((cb) =>
            cb.id === id ? { ...cb, balance: cb.balance + amount } : cb
          ),
        }))
      },

      addCashMovement: (movement) => {
        const newMovement = {
          ...movement,
          id: (movement as any).id || `cm${Date.now()}`,
          createdAt: (movement as any).createdAt || new Date().toISOString().split('T')[0],
        }
        set((state) => ({ cashMovements: [...state.cashMovements, newMovement as CashMovement] }))

        // Update cash box balance ONLY if not pending
        if (movement.status !== 'pending') {
          const multiplier = movement.type === 'income' || movement.type === 'transfer_in' ? 1 : -1
          get().updateCashBoxBalance(movement.cashBoxId, movement.amount * multiplier)
        }
      },

      updateCashMovement: (id, data) => {
        const { cashMovements } = get()
        const oldMovement = cashMovements.find(m => m.id === id)
        if (!oldMovement) return

        set((state) => ({
          cashMovements: state.cashMovements.map((m) => (m.id === id ? { ...m, ...data } : m)),
        }))

        // Handle balance changes based on status change
        if (data.status === 'approved' && oldMovement.status === 'pending') {
          // Now it counts, so apply the balance change
          const multiplier = oldMovement.type === 'income' || oldMovement.type === 'transfer_in' ? 1 : -1
          get().updateCashBoxBalance(oldMovement.cashBoxId, oldMovement.amount * multiplier)
        } else if (data.status === 'rejected' && oldMovement.status === 'approved') {
          // Revert the balance change
          const multiplier = oldMovement.type === 'income' || oldMovement.type === 'transfer_in' ? -1 : 1
          get().updateCashBoxBalance(oldMovement.cashBoxId, oldMovement.amount * multiplier)
        }
      },

      deleteCashMovement: (id) => {
        const movement = get().cashMovements.find(m => m.id === id)
        if (movement) {
          // Reverse the balance change ONLY if it was approved (and thus counted)
          // If it was pending, it was never counted, so don't revert.
          if (movement.status !== 'pending' && movement.status !== 'rejected') {
            const multiplier = movement.type === 'income' || movement.type === 'transfer_in' ? -1 : 1
            get().updateCashBoxBalance(movement.cashBoxId, movement.amount * multiplier)
          }
        }
        set((state) => ({
          cashMovements: state.cashMovements.filter((m) => m.id !== id),
          cashMovementsMeta: state.cashMovementsMeta ? {
            ...state.cashMovementsMeta,
            totalCount: state.cashMovementsMeta.totalCount - 1
          } : null
        }))
      },

      deleteCashBox: (id) => {
        set((state) => ({
          cashBoxes: state.cashBoxes.filter((cb) => cb.id !== id),
          // Also delete all movements associated with this cash box
          cashMovements: state.cashMovements.filter((m) => m.cashBoxId !== id),
        }))
      },

      transferFunds: (fromBoxId, toBoxId, amount, userId) => {
        const today = new Date().toISOString().split('T')[0]
        const state = get()
        const fromBox = state.cashBoxes.find(b => b.id === fromBoxId)
        const toBox = state.cashBoxes.find(b => b.id === toBoxId)

        const fromName = fromBox ? fromBox.name : "Caja Origen"
        const toName = toBox ? toBox.name : "Caja Destino"

        // Create transfer out movement
        get().addCashMovement({
          cashBoxId: fromBoxId,
          type: 'transfer_out',
          amount,
          category: 'assignment',
          description: `Transferencia a ${toName}`,
          date: today,
          userId,
          status: 'approved',
        })

        // Create transfer in movement  
        get().addCashMovement({
          cashBoxId: toBoxId,
          type: 'transfer_in',
          amount,
          category: 'assignment',
          description: `Transferencia recibida de ${fromName}`,
          date: today,
          userId,
          status: 'approved',
        })
      },

      getDriverCashBox: (userId) => {
        return get().cashBoxes.find(cb => cb.userId === userId && cb.type === 'driver')
      },

      getGeneralCashBox: () => {
        return get().cashBoxes.find(cb => cb.type === 'general')
      },

      addReport: (report) => {
        const newReport: Report = {
          ...report,
          id: `r${Date.now()}`,
          status: 'pending',
          createdAt: new Date().toISOString().split('T')[0],
        }
        set((state) => ({ reports: [...state.reports, newReport] }))
      },

      updateReport: (id, report) => {
        set((state) => ({
          reports: state.reports.map((r) => (r.id === id ? { ...r, ...report } : r)),
        }))
      },

      deleteReport: (id) => {
        set((state) => ({
          reports: state.reports.filter((r) => r.id !== id),
        }))
      },

      setReportsFromDb: (reports) => {
        if (reports && reports.data) {
          set({ reports: reports.data })
        } else {
          set({ reports: Array.isArray(reports) ? reports : [] })
        }
      },

      updateNotificationSettings: (settings) => {
        set((state) => ({
          notificationSettings: { ...state.notificationSettings, ...settings },
        }))
      },

      // Database sync setters
      setUsersFromDb: (users) => set({ users }),
      setVehiclesFromDb: (vehicles) => set({ vehicles }),
      setMaintenanceFromDb: (records) => {
        if (records && records.data) {
          set({ maintenanceRecords: records.data })
        } else {
          set({ maintenanceRecords: Array.isArray(records) ? records : [] })
        }
      },
      setPurchasesFromDb: (purchases) => {
        if (purchases && purchases.data) {
          set({ purchases: purchases.data })
        } else {
          set({ purchases: Array.isArray(purchases) ? purchases : [] })
        }
      },
      setAlertsFromDb: (alerts) => set({ alerts }),
      setCashBoxesFromDb: (cashBoxes) => set({ cashBoxes }),
      setCashMovementsFromDb: (movementsData: any) => {
        if (movementsData.data && movementsData.meta) {
          set({
            cashMovements: movementsData.data,
            cashMovementsMeta: movementsData.meta
          })
        } else {
          set({ cashMovements: movementsData })
        }
      },
      setPendingMovementsFromDb: (movementsData: any) => {
        if (movementsData && movementsData.data) {
          set({ pendingMovements: movementsData.data })
        } else {
          set({ pendingMovements: Array.isArray(movementsData) ? movementsData : [] })
        }
      },
      setInspectionTicketsFromDb: (tickets) => {
        if (tickets && tickets.data) {
          set({ inspectionTickets: tickets.data })
        } else {
          set({ inspectionTickets: Array.isArray(tickets) ? tickets : [] })
        }
      },

      exportAllData: () => {
        const state = get()
        return {
          vehicles: state.vehicles,
          maintenanceRecords: state.maintenanceRecords,
          purchases: state.purchases,
          alerts: state.alerts,
          calendarEvents: state.calendarEvents,
        }
      },

      importAllData: (data) => {
        set((state) => ({
          vehicles: data.vehicles || state.vehicles,
          maintenanceRecords: data.maintenanceRecords || state.maintenanceRecords,
          purchases: data.purchases || state.purchases,
          alerts: data.alerts || state.alerts,
          calendarEvents: data.calendarEvents || state.calendarEvents,
        }))
      },

      clearAllData: () => {
        set({
          vehicles: [],
          maintenanceRecords: [],
          purchases: [],
          alerts: [],
          calendarEvents: [],
        })
      },
      addZone: (zone) => {
        set((state) => ({ zones: [...state.zones, zone] }))
      },

      deleteZone: (id) => {
        set((state) => ({ zones: state.zones.filter((z) => z.id !== id) }))
      },

      setZonesFromDb: (zones) => set({ zones }),
    }),
    {
      name: "fleet-manager-storage-v3",
      // Exclude vehicles and other heavy data from LocalStorage to avoid QuotaExceededError
      // preventing base64 images from crashing the app storage.
      partialize: (state) => ({
        user: state.user,
        users: state.users,
        isAuthenticated: state.isAuthenticated,
        notificationSettings: state.notificationSettings,
        // We do NOT persist vehicles, maintenanceRecords, etc. here 
        // because they should be loaded from the Database on mount.
      }),
      onRehydrateStorage: () => (state) => {
        state?.setHasHydrated(true)
      },
    },
  ),
)
